import React, { useState, useEffect } from 'react';
import apiClient, { roomsAPI } from './services/api';
import RoomSelectionModal from './RoomSelectionModal';
import './MultiRoomCalendar.css';

interface RoomAvailability {
  [roomId: string]: {
    isOccupied: boolean;
    price: number;
  };
}

interface CalendarDay {
  date: string;
  totalRooms: number;
  occupiedCount: number;
  availableCount: number;
  status: string;
  roomAvailability: RoomAvailability;
  priceRange: {
    min: number;
    max: number;
  } | null;
}

interface Room {
  id: string;
  name: string;
  roomType: string;
  basePrice: number;
}

interface CalendarData {
  year: number;
  month: number;
  monthName: string;
  calendar: { [key: string]: CalendarDay };
  rooms: Room[];
  summary: { totalDays: number; totalRooms: number };
}

interface BlockedDateEntry {
  date: string;
  reason?: string;
  affects?: string[];
}

const DAYS_PER_VIEW = 16;
const PRICE_WINDOW_CACHE_TTL_MS = 2 * 60 * 1000;
const windowPriceCache = new Map<string, { expiresAt: number; byDate: Record<string, Record<string, number>> }>();

const EMPTY_CHILD_AGES: number[] = [];

const parseChildAgesKey = (key: string): number[] => {
  if (!key) return EMPTY_CHILD_AGES;
  return key
    .split(',')
    .map((value) => Number(value))
    .filter((age) => Number.isFinite(age) && age >= 0);
};

const normalizeDateKey = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    // Admin kaydı bazen YYYY-MM-DD, bazen ISO string gelebilir.
    return value.includes('T') ? value.split('T')[0] : value.slice(0, 10);
  }
  const d = new Date(value as any);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface MultiRoomCalendarProps {
  initialAdults?: number;
  initialChildAges?: number[];
  onGuestConfigChange?: (data: { adults: number; childAges: number[] }) => void;
  onReservationSelect?: (data: {
    roomId: string;
    roomName: string;
    checkInDate: string;
    checkOutDate: string;
    adults: number;
    children: number;
    childAges: number[];
    totalPrice: number;
    nights: number;
  }) => void;
}

const MultiRoomCalendar: React.FC<MultiRoomCalendarProps> = ({
  initialAdults = 2,
  initialChildAges,
  onGuestConfigChange,
  onReservationSelect
}) => {
  const initialChildAgesKey = Array.isArray(initialChildAges) ? initialChildAges.join(',') : '';
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedCheckIn, setSelectedCheckIn] = useState<string>('');
  const [selectedCheckOut, setSelectedCheckOut] = useState<string>('');
  const [dateSelectionStep, setDateSelectionStep] = useState<'room' | 'check-in' | 'check-out'>('room');
  const [blockedDates, setBlockedDates] = useState<BlockedDateEntry[]>([]);
  const [dayWindowStart, setDayWindowStart] = useState(0);
  const [windowPrices, setWindowPrices] = useState<Record<string, Record<string, number>>>({});
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceLoadingVisible, setPriceLoadingVisible] = useState(false);
  const [guestAdults, setGuestAdults] = useState<number>(Math.max(1, initialAdults || 2));
  const [guestChildAges, setGuestChildAges] = useState<number[]>(parseChildAgesKey(initialChildAgesKey));
  const guestChildAgesKey = guestChildAges.join(',');
  
  // Mobile daily room list modal state
  const [dailyRoomListOpen, setDailyRoomListOpen] = useState(false);
  const [dailyListSelectedDate, setDailyListSelectedDate] = useState<string>('');
  const [dailyListRooms, setDailyListRooms] = useState<any[]>([]);
  const [dailyListLoading, setDailyListLoading] = useState(false);

  useEffect(() => {
    fetchCalendar();
  }, [currentMonth]);

  useEffect(() => {
    setGuestAdults(Math.max(1, initialAdults || 2));
  }, [initialAdults]);

  useEffect(() => {
    setGuestChildAges(parseChildAgesKey(initialChildAgesKey));
  }, [initialChildAgesKey]);

  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    if (priceLoading) {
      showTimer = window.setTimeout(() => {
        setPriceLoadingVisible(true);
      }, 180);
    } else {
      hideTimer = window.setTimeout(() => {
        setPriceLoadingVisible(false);
      }, 120);
    }

    return () => {
      if (showTimer) window.clearTimeout(showTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, [priceLoading]);

  useEffect(() => {
    if (!calendarData) {
      setWindowPrices({});
      return;
    }

    const dates = Object.keys(calendarData.calendar).sort();
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const isCurrentMonthView = currentMonth === currentMonthStr;
    const visibleDates = isCurrentMonthView ? dates.filter((d) => d >= todayStr) : dates;
    const maxWindowStart = Math.max(0, visibleDates.length - DAYS_PER_VIEW);
    const effectiveWindowStart = Math.min(dayWindowStart, maxWindowStart);
    const windowDates = visibleDates.slice(effectiveWindowStart, effectiveWindowStart + DAYS_PER_VIEW);

    if (windowDates.length === 0 || (calendarData.rooms || []).length === 0) {
      setWindowPrices({});
      return;
    }

    let cancelled = false;

    const fetchWindowPrices = async () => {
      const windowCacheKey = `${windowDates.join(',')}|${guestAdults}|${guestChildAges.length}|${guestChildAgesKey}`;
      const cachedWindow = windowPriceCache.get(windowCacheKey);

      // Cache hit → update prices silently, no loading flash
      if (cachedWindow && cachedWindow.expiresAt > Date.now()) {
        if (!cancelled) {
          setWindowPrices(cachedWindow.byDate);
        }
        return;
      }

      setPriceLoading(true);
      try {
        const response = await roomsAPI.getAvailabilityWindow({
          startDate: windowDates[0],
          days: windowDates.length,
          adults: guestAdults,
          children: guestChildAges.length,
          childrenAges: guestChildAgesKey
        });

        const availabilityByDate = response.data?.data?.availabilityByDate || {};
        const nextPrices: Record<string, Record<string, number>> = {};

        windowDates.forEach((date) => {
          const options = Array.isArray(availabilityByDate?.[date]) ? availabilityByDate[date] : [];
          const byRoom: Record<string, number> = {};

          (calendarData.rooms || []).forEach((room) => {
            const matched = options.find((opt: any) =>
              opt?.roomTypeId === room.id || opt?.roomId === room.id || opt?.id === room.id
            );
            if (typeof matched?.price === 'number' && matched.price > 0) {
              byRoom[room.id] = matched.price;
            }
          });

          nextPrices[date] = byRoom;
        });

        windowPriceCache.set(windowCacheKey, {
          expiresAt: Date.now() + PRICE_WINDOW_CACHE_TTL_MS,
          byDate: nextPrices
        });

        if (cancelled) return;
        setWindowPrices(nextPrices);
      } catch {
        if (!cancelled) {
          setWindowPrices({});
        }
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    };

    fetchWindowPrices();

    return () => {
      cancelled = true;
    };
  }, [calendarData, currentMonth, dayWindowStart, guestAdults, guestChildAges.length, guestChildAgesKey]);

  const emitGuestConfig = (adults: number, ages: number[]) => {
    if (onGuestConfigChange) {
      onGuestConfigChange({ adults, childAges: ages });
    }
  };

  const handleGuestAdultsChange = (value: number) => {
    const nextAdults = Math.max(1, value);
    setGuestAdults(nextAdults);
    emitGuestConfig(nextAdults, guestChildAges);
  };

  const handleGuestChildrenCountChange = (count: number) => {
    const nextCount = Math.max(0, count);
    const nextAges =
      nextCount > guestChildAges.length
        ? [...guestChildAges, ...Array(nextCount - guestChildAges.length).fill(7)]
        : guestChildAges.slice(0, nextCount);

    setGuestChildAges(nextAges);
    emitGuestConfig(guestAdults, nextAges);
  };

  const handleGuestChildAgeChange = (index: number, age: number) => {
    const nextAges = [...guestChildAges];
    nextAges[index] = age;
    setGuestChildAges(nextAges);
    emitGuestConfig(guestAdults, nextAges);
  };

  const fetchCalendar = async () => {
    try {
      setLoading(true);
      setError(null);

      const [calendarRes, blockedDatesRes] = await Promise.all([
        apiClient.get('/rooms/calendar', {
          params: { month: currentMonth }
        }),
        apiClient.get('/products/blocked-dates')
      ]);

      if (calendarRes.data.success) {
        setCalendarData(calendarRes.data.data);

        if (blockedDatesRes.data?.success && Array.isArray(blockedDatesRes.data?.data)) {
          setBlockedDates(blockedDatesRes.data.data);
        } else {
          setBlockedDates([]);
        }
      } else {
        setError('Takvim yüklenemedi');
      }
    } catch (err: any) {
      console.error('Calendar fetch error:', err);
      setError(err.response?.data?.message || 'Takvim yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = currentMonth.split('-').map(Number);
    let newYear = year;
    let newMonth = month + (direction === 'next' ? 1 : -1);

    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }

    // Ay değişirken seçimi KORUYORUZ - silmiyoruz!
    // Sadece ay verisini güncelliyoruz
    setCurrentMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
    setDayWindowStart(0);
  };

  // Check-in'in hangi ayda olduğunu kontrol et
  const getCheckInMonth = () => {
    if (!selectedCheckIn) return null;
    const date = new Date(selectedCheckIn);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const getBlockedDateEntry = (date: string): BlockedDateEntry | null => {
    return blockedDates.find((entry) => {
      if (normalizeDateKey(entry.date) !== normalizeDateKey(date)) return false;
      if (!entry.affects || entry.affects.length === 0) return true;
      return entry.affects.includes('all') || entry.affects.includes('rooms');
    }) || null;
  };

  const handleDayClick = (room: Room, date: string, dayStatus: 'available' | 'occupied' | 'past' | 'blocked') => {
    if (priceLoading) {
      return;
    }

    // Geçmiş tarihler için tıklama engelle (string karşılaştırma - timezone güvenli)
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    if (date < todayStr) {
      return;
    }

    // Mobile flow: show daily room list modal if first selection and available
    const isMobile = window.innerWidth < 768;
    if (isMobile && !selectedCheckIn && dayStatus === 'available' && !selectedCheckOut) {
      const clickedRoomId = room.id;
      setDailyListSelectedDate(date);
      setDailyListLoading(true);
      setDailyRoomListOpen(true);
      
      // Fetch available rooms for this date
      (async () => {
        try {
          const response = await roomsAPI.getAvailabilityWindow({
            startDate: date,
            days: 1,
            adults: guestAdults,
            children: guestChildAges.length,
            childrenAges: guestChildAgesKey
          });
          
          const dailyRooms = response.data?.data?.availabilityByDate?.[date] || [];
          const filtered = dailyRooms.filter((r: any) => r.available && typeof r.price === 'number' && r.price > 0);
          // Sort: tıklanan oda en üste
          filtered.sort((a: any, b: any) => {
            const aId = a.roomTypeId || a.id;
            const bId = b.roomTypeId || b.id;
            if (aId === clickedRoomId) return -1;
            if (bId === clickedRoomId) return 1;
            return 0;
          });
          setDailyListRooms(filtered);
        } catch (err) {
          console.error('Error fetching daily rooms:', err);
          setDailyListRooms([]);
        } finally {
          setDailyListLoading(false);
        }
      })();
      
      return;
    }

    // Seçili check-in tarihine tekrar tıklanırsa seçimi iptal et
    if (selectedRoom && selectedRoom.id === room.id && selectedCheckIn === date && !selectedCheckOut) {
      setSelectedRoom(null as any);
      setSelectedCheckIn('');
      setSelectedCheckOut('');
      setDateSelectionStep('room');
      console.log('🔄 Seçim iptal edildi');
      return;
    }

    // İlk tıklama: Oda ve check-in seç (dolu günler için engelle)
    if (!selectedRoom || selectedRoom.id !== room.id || !selectedCheckIn) {
      // Check-in tarihi için müsait gün şart
      if (dayStatus === 'blocked') {
        const blockedEntry = getBlockedDateEntry(date);
        const reason = blockedEntry?.reason ? ` (${blockedEntry.reason})` : '';
        alert(`⚠️ Seçilen tarih kapalıdır${reason}. Lütfen farklı tarih seçiniz.`);
        console.log('🚫 Giriş tarihi kapalı gün:', date);
        return;
      }

      if (dayStatus === 'occupied') {
        console.log('❌ Giriş tarihi olarak dolu gün seçilemez:', date);
        return;
      }

      // Check if price is available for this date and room
      const dayPrice = windowPrices?.[date]?.[room.id] ?? calendarData?.calendar?.[date]?.roomAvailability?.[room.id]?.price;
      if (!dayPrice || typeof dayPrice !== 'number' || dayPrice <= 0) {
        console.log('⚠️ Bu tarih için oda fiyatı bulunamadı:', date, room.id);
        return;
      }

      setSelectedRoom(room);
      setSelectedCheckIn(date);
      setSelectedCheckOut('');
      setDateSelectionStep('check-out');
      console.log('📅 Check-in seçildi:', date, 'Şimdi check-out seçin');
      return;
    }

    // Farklı odaya tıklandıysa, yeni seçim başlat
    if (selectedRoom.id !== room.id) {
      // Yeni oda seçimi sadece müsait günlerde
      if (dayStatus === 'blocked') {
        const blockedEntry = getBlockedDateEntry(date);
        const reason = blockedEntry?.reason ? ` (${blockedEntry.reason})` : '';
        alert(`⚠️ Seçilen tarih kapalıdır${reason}. Lütfen farklı tarih seçiniz.`);
        console.log('🚫 Farklı oda seçiminde kapalı gün:', date);
        return;
      }

      if (dayStatus === 'occupied') {
        console.log('❌ Farklı oda seçmek için müsait gün seçin:', date);
        return;
      }

      setSelectedRoom(room);
      setSelectedCheckIn(date);
      setSelectedCheckOut('');
      setDateSelectionStep('check-out');
      console.log('📅 Farklı oda seçildi, yeni check-in:', date);
      return;
    }

    // İkinci tıklama: Check-out seç (aynı oda)
    // Not: Check-out tarihi dolu bile olabilir (başka guest 15:00'de check-in edecekse sabah checkout olur)
    if (selectedCheckIn && !selectedCheckOut) {
      if (dayStatus === 'blocked') {
        const blockedEntry = getBlockedDateEntry(date);
        const reason = blockedEntry?.reason ? ` (${blockedEntry.reason})` : '';
        alert(`⚠️ Seçilen tarih kapalıdır${reason}. Lütfen farklı tarih seçiniz.`);
        console.log('🚫 Çıkış tarihi olarak kapalı gün seçilemez:', date);
        return;
      }

      const checkInDate = new Date(selectedCheckIn);
      const checkOutDate = new Date(date);

      // Check-out, check-in'den sonra olmalı (en az 1 gece)
      if (checkOutDate <= checkInDate) {
        alert('Çıkış tarihi, giriş tarihinden sonra olmalıdır');
        return;
      }

      // NOT: Ay geçişli rezervasyonlarda aralık kontrolü yapamıyoruz 
      // çünkü diğer ayın verileri yok. Bu durumda AsistPro'da kontrol edilecek.
      const checkInMonth = getCheckInMonth();
      if (checkInMonth !== currentMonth) {
        // Farklı ay - doğrudan onayla, AsistPro kontrol edecek
        console.log('📅 Ay geçişli rezervasyon:', selectedCheckIn, '→', date);
      } else {
        // Aynı ay - aralıktaki günlerde dolu var mı kontrol et
        // CHECK-IN'DEN SONRAKİ GÜNLERI kontrol et (CHECK-OUT'U HARİÇ TUTA)
        let hasOccupiedInRange = false;
        const currentDate = new Date(checkInDate);
        currentDate.setDate(currentDate.getDate() + 1);

        while (currentDate < checkOutDate) {
          const dateStr = currentDate.toISOString().split('T')[0];
          const statusInRange = getDayStatus(dateStr, room.id);
          if (statusInRange !== 'available') {
            hasOccupiedInRange = true;
            if (statusInRange === 'blocked') {
              const blockedEntry = getBlockedDateEntry(dateStr);
              console.log('🚫 Tarih aralığında kapalı gün bulundu:', dateStr, blockedEntry?.reason || 'Neden yok');
            } else {
              console.log('❌ Tarih aralığında dolu gün bulundu:', dateStr);
            }
            break;
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        if (hasOccupiedInRange) {
          alert('Seçilen tarih aralığında satışa kapalı veya dolu günler var. Lütfen farklı tarihler seçin.');
          setSelectedCheckIn('');
          setSelectedCheckOut('');
          setDateSelectionStep('room');
          return;
        }
      }

      setSelectedCheckOut(date);
      setDateSelectionStep('room');
      setModalOpen(true);
      console.log('✅ Check-out seçildi:', date, '→ Modal açılıyor');
    }
  };

  const handleModalConfirm = (data: {
    roomId: string;
    checkInDate: string;
    checkOutDate: string;
    adults: number;
    children: number;
    childAges: number[];
  }) => {
    setModalOpen(false);
    
    // Seçimleri sıfırla - BookingFormModal kendi verileriyle açılacak
    setSelectedCheckIn('');
    setSelectedCheckOut('');
    setSelectedRoom(null);
    setDateSelectionStep('room');
    
    // Calculate nights and total price
    const checkIn = new Date(data.checkInDate);
    const checkOut = new Date(data.checkOutDate);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    
    // Get room details for price
    const room = calendarData?.rooms.find(r => r.id === data.roomId);
    const roomName = room?.name || 'Oda';
    
    // Calculate total price from selected date range
    let totalPrice = 0;
    if (calendarData) {
      const dates: string[] = [];
      const current = new Date(data.checkInDate);
      const end = new Date(data.checkOutDate);
      
      while (current < end) {
        const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
        dates.push(dateStr);
        current.setDate(current.getDate() + 1);
      }
      
      totalPrice = dates.reduce((sum, date) => {
        const dayData = calendarData.calendar[date];
        const roomPrice = dayData?.roomAvailability?.[data.roomId]?.price || room?.basePrice || 0;
        return sum + roomPrice;
      }, 0);
    }
    
    // Callback'i çağır (OurRooms component'i bu veriyi ReservationPage'e aktaracak)
    if (onReservationSelect) {
      onReservationSelect({
        ...data,
        roomName,
        totalPrice,
        nights
      });
    }
  };

  const getDayStatus = (date: string, roomId: string): 'available' | 'occupied' | 'past' | 'blocked' => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    if (date < todayStr) return 'past';

    if (getBlockedDateEntry(date)) return 'blocked';

    const day = calendarData?.calendar[date];
    if (!day) return 'past';

    const roomStatus = day.roomAvailability?.[roomId];
    return roomStatus?.isOccupied ? 'occupied' : 'available';
  };

  const formatCellPrice = (price: number): string => {
    if (price >= 1000) {
      return `₺${(price / 1000).toFixed(1).replace('.0', '')}K`;
    }
    return `₺${Math.round(price)}`;
  };

  // Check-in'den sonra o odada ilk müsait olmayan günü bul (occupied veya blocked)
  const getFirstUnavailableAfter = (afterDate: string, roomId: string): string | null => {
    if (!calendarData) return null;
    const sortedDates = Object.keys(calendarData.calendar).sort();
    for (const d of sortedDates) {
      if (d <= afterDate) continue;
      if (getDayStatus(d, roomId) !== 'available') return d;
    }
    return null;
  };



  if (loading) {
    return (
      <div className="multi-room-calendar-loading">
        <div className="spinner"></div>
        <p>Takvim yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="multi-room-calendar-error">
        <p>❌ {error}</p>
        <button onClick={fetchCalendar} className="retry-button">Tekrar Dene</button>
      </div>
    );
  }

  if (!calendarData) {
    return null;
  }

  // Günleri matrix için hazırla
  const dates = Object.keys(calendarData.calendar).sort();
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonthView = currentMonth === currentMonthStr;
  const visibleDates = isCurrentMonthView
    ? dates.filter((d) => d >= todayStr)
    : dates;
  const totalVisibleDays = visibleDates.length;
  const maxWindowStart = Math.max(0, totalVisibleDays - DAYS_PER_VIEW);
  const effectiveWindowStart = Math.min(dayWindowStart, maxWindowStart);
  const pagedVisibleDates = visibleDates.slice(effectiveWindowStart, effectiveWindowStart + DAYS_PER_VIEW);
  const visibleRangeStart = totalVisibleDays === 0 ? 0 : effectiveWindowStart + 1;
  const visibleRangeEnd = Math.min(totalVisibleDays, effectiveWindowStart + DAYS_PER_VIEW);
  const canPagePrev = effectiveWindowStart > 0;
  const canPageNext = effectiveWindowStart + DAYS_PER_VIEW < totalVisibleDays;
  const rooms = calendarData.rooms || [];

  return (
    <div className="multi-room-calendar-container">
      {/* Header */}
      <div className="calendar-header">
        <button onClick={() => navigateMonth('prev')} className="nav-button">
          ← Önceki Ay
        </button>
        <h3>{calendarData.monthName}</h3>
        <button onClick={() => navigateMonth('next')} className="nav-button">
          Sonraki Ay →
        </button>
      </div>

      {/* Açıklama */}
      <div className="calendar-guest-panel">
        <div className="calendar-guest-title">Konaklama Tercihi</div>
        <div className="calendar-guest-row">
          <div className="calendar-guest-field">
            <label>Yetişkin</label>
            <select
              value={guestAdults}
              onChange={(e) => handleGuestAdultsChange(parseInt(e.target.value, 10))}
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="calendar-guest-field">
            <label>Çocuk</label>
            <select
              value={guestChildAges.length}
              onChange={(e) => handleGuestChildrenCountChange(parseInt(e.target.value, 10))}
            >
              {[0, 1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        {guestChildAges.length > 0 && (
          <div className="calendar-child-ages">
            {guestChildAges.map((age, index) => (
              <div key={index} className="calendar-guest-field calendar-guest-field-small">
                <label>{index + 1}. Çocuk Yaşı</label>
                <select
                  value={age}
                  onChange={(e) => handleGuestChildAgeChange(index, parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: 18 }, (_, i) => i).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-dot available"></span>
          <span>Müsait</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot occupied"></span>
          <span>Dolu</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot blocked"></span>
          <span>Satışa Kapalı</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot past"></span>
          <span>Geçmiş</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot" style={{background: 'linear-gradient(135deg, #40916c 50%, #e63946 50%)'}}></span>
          <span>Sadece 1 gece</span>
        </div>
        {selectedCheckIn && !selectedCheckOut && selectedRoom && (
          <div className="legend-item" style={{backgroundColor: '#dbeafe', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            <span className="legend-dot" style={{backgroundColor: '#3b82f6'}}></span>
            <span>
              <strong>{selectedRoom.name}</strong> • Giriş: {new Date(selectedCheckIn).toLocaleDateString('tr-TR')} → Çıkış seçin
            </span>
            <button
              onClick={() => {
                setSelectedRoom(null as any);
                setSelectedCheckIn('');
                setSelectedCheckOut('');
                setDateSelectionStep('room');
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#dc3545', fontWeight: 'bold', fontSize: '1rem',
                padding: '0 4px', lineHeight: 1, marginLeft: '4px'
              }}
              title="Seçimi iptal et"
            >✕</button>
          </div>
        )}
        <p className="legend-note">
          💡 {selectedCheckIn && !selectedCheckOut 
            ? 'Çıkış tarihini seçin'
            : 'Önce giriş, sonra çıkış tarihi seçin'
          }
          {priceLoading ? ' • Fiyatlar güncelleniyor...' : ''}
        </p>
      </div>

      {priceLoading && (
        <div className="calendar-price-loading-banner" role="status" aria-live="polite">
          <span className="calendar-price-loading-spinner" aria-hidden="true"></span>
          <span>Güncel fiyatlar çekiliyor, lütfen bekleyin...</span>
        </div>
      )}

      <div className={`calendar-interaction-zone${priceLoading ? ' is-price-loading' : ''}`}>
        {totalVisibleDays > DAYS_PER_VIEW && (
          <div className="date-window-controls">
            <button
              type="button"
              className="date-window-button"
              onClick={() => setDayWindowStart((prev) => Math.max(0, prev - DAYS_PER_VIEW))}
              disabled={!canPagePrev || priceLoading}
            >
              ← {DAYS_PER_VIEW} Gün Geri
            </button>
            <div className="date-window-info">
              {visibleRangeStart}-{visibleRangeEnd} / {totalVisibleDays} gün
            </div>
            <button
              type="button"
              className="date-window-button"
              onClick={() => setDayWindowStart((prev) => Math.min(maxWindowStart, prev + DAYS_PER_VIEW))}
              disabled={!canPageNext || priceLoading}
            >
              {DAYS_PER_VIEW} Gün İleri →
            </button>
          </div>
        )}

        {/* Matrix Takvim */}
        <div className="multi-room-calendar-scroll">
          <div
            className="multi-room-calendar"
            style={{ ['--days-count' as any]: String(pagedVisibleDates.length) }}
          >
            {/* Header Row - Günler */}
            <div className="calendar-grid-header">
              <div className="room-name-header">Oda</div>
              {pagedVisibleDates.map(date => {
                const day = new Date(date);
                return (
                  <div key={date} className="day-header">
                    <div className="day-number">{day.getDate()}</div>
                    <div className="day-name">
                      {day.toLocaleDateString('tr-TR', { weekday: 'short' })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Her oda için satır */}
            {rooms.map(room => {
              // Checkout seçim fazı: bu oda için boundary hesapla
              const checkoutBoundary = (selectedCheckIn && !selectedCheckOut && selectedRoom?.id === room.id)
                ? getFirstUnavailableAfter(selectedCheckIn, room.id)
                : null;

              return (
                <div key={room.id} className="room-row">
                  <div className="room-name-cell">
                    <div className="room-name-text">{room.name}</div>
                    <div className="room-type-text">{room.roomType}</div>
                  </div>
                  {pagedVisibleDates.map(date => {
                    const status = getDayStatus(date, room.id);
                    const dayPrice = windowPrices?.[date]?.[room.id] ?? calendarData.calendar?.[date]?.roomAvailability?.[room.id]?.price;
                    const showPriceBadge = status === 'available' && typeof dayPrice === 'number' && dayPrice > 0;
                    const isCheckIn = selectedCheckIn === date && selectedRoom?.id === room.id;
                    const isCheckOut = selectedCheckOut === date && selectedRoom?.id === room.id;

                    // Checkout seçim fazı: checkIn ile boundary arasındaki müsait günler
                    const isInRange = !!(selectedCheckIn && !selectedCheckOut && selectedRoom?.id === room.id &&
                                      date > selectedCheckIn &&
                                      (checkoutBoundary ? date < checkoutBoundary : true));

                    // Boundary: split yeşil/kırmızı - checkout yapılabilir ama ertesi gün dolu
                    const isCheckoutBoundary = !!(checkoutBoundary === date && selectedCheckIn && !selectedCheckOut);

                    // Base view: müsait ama ertesi günü dolu (henüz seçim yapılmadıysa)
                    const nextDateStr = new Date(new Date(date + 'T00:00:00').getTime() + 86400000).toISOString().split('T')[0];
                    const nextDayStatus = getDayStatus(nextDateStr, room.id);
                    const isLastAvailable = !selectedCheckIn && status === 'available' && (nextDayStatus === 'occupied' || nextDayStatus === 'blocked');

                    let cellClass = `room-day-cell ${status}`;
                    if (isCheckIn) cellClass += ' check-in-selected';
                    if (isCheckOut) cellClass += ' check-out-selected';
                    if (isInRange) cellClass += ' in-range';
                    if (isCheckoutBoundary) cellClass += ' checkout-boundary';
                    if (isLastAvailable) cellClass += ' last-available';

                    return (
                      <div
                        key={date}
                        className={cellClass}
                        onClick={() => handleDayClick(room, date, status)}
                        role={status === 'available' || (isCheckoutBoundary && status !== 'blocked') ? 'button' : undefined}
                        tabIndex={status === 'available' || (isCheckoutBoundary && status !== 'blocked') ? 0 : undefined}
                        title={
                          isCheckIn ? 'Giriş tarihi (Check-out seçin)' :
                          (isCheckoutBoundary && status === 'blocked') ? `Satışa kapalı${getBlockedDateEntry(date)?.reason ? ` (${getBlockedDateEntry(date)?.reason})` : ''}` :
                          isCheckoutBoundary ? '⚠️ Çıkış tarihi (ertesi gün dolu)' :
                          isLastAvailable ? '⚠️ Sadece 1 gecelik konaklama' :
                          isCheckOut ? 'Çıkış tarihi' :
                          status === 'available' ? 'Tıklayın' :
                          status === 'blocked' ? `Satışa kapalı${getBlockedDateEntry(date)?.reason ? ` (${getBlockedDateEntry(date)?.reason})` : ''}` :
                          status === 'occupied' ? 'Dolu' :
                          'Geçmiş tarih'
                        }
                      >
                        {showPriceBadge && (
                          <span className="room-day-price-badge">{formatCellPrice(dayPrice)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {priceLoadingVisible && !dailyRoomListOpen && (
          <div className="calendar-price-loading-overlay" aria-hidden="true">
            <div className="calendar-price-loading-chip">
              <span className="calendar-price-loading-spinner"></span>
              <span>Fiyatlar güncelleniyor</span>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedRoom && selectedCheckIn && selectedCheckOut && (
        <RoomSelectionModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            // Reset selections
            setSelectedCheckIn('');
            setSelectedCheckOut('');
            setSelectedRoom(null);
            setDateSelectionStep('room');
          }}
          roomId={selectedRoom.id}
          roomName={selectedRoom.name}
          checkInDate={selectedCheckIn}
          checkOutDate={selectedCheckOut}
          initialAdults={guestAdults}
          initialChildren={guestChildAges.length}
          initialChildAges={guestChildAges}
          onConfirm={handleModalConfirm}
        />
      )}

      {/* Mobile Daily Room List Modal */}
      {dailyRoomListOpen && (
        <div className="daily-room-list-modal-overlay" onClick={() => setDailyRoomListOpen(false)}>
          <div className="daily-room-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="daily-room-list-header">
              <h3>
                {new Date(dailyListSelectedDate + 'T00:00:00').toLocaleDateString('tr-TR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <button
                className="daily-room-list-close"
                onClick={() => setDailyRoomListOpen(false)}
                title="Kapat"
              >
                ✕
              </button>
            </div>

            <div className="daily-room-list-content">
              {dailyListLoading ? (
                <div className="daily-room-list-loading">
                  <div className="spinner"></div>
                  <p>Odalar yükleniyor...</p>
                </div>
              ) : dailyListRooms.length > 0 ? (
                <div className="daily-room-cards">
                  {dailyListRooms.map((room: any) => (
                    <div key={room.roomTypeId || room.id} className="daily-room-card">
                      {room.imageUrl && (
                        <div className="daily-room-card-image">
                          <img src={room.imageUrl} alt={room.roomTypeName} />
                        </div>
                      )}
                      <div className="daily-room-card-body">
                        <h4>{room.roomTypeName}</h4>
                        <p className="daily-room-card-desc">{room.description}</p>
                        <div className="daily-room-card-features">
                          {room.maxOccupancy && (
                            <span className="feature">👥 {room.maxOccupancy} kişi</span>
                          )}
                          {room.floor && (
                            <span className="feature">🏢 {room.floor}. kat</span>
                          )}
                          {room.viewType && (
                            <span className="feature">🌊 {room.viewType}</span>
                          )}
                        </div>
                        <div className="daily-room-card-footer">
                          <div className="daily-room-price">
                            <span className="price-value">₺{room.price?.toLocaleString('tr-TR')}</span>
                          </div>
                          <button
                            className="daily-room-card-button"
                            onClick={() => {
                              setSelectedRoom({
                                id: room.roomTypeId || room.id,
                                name: room.roomTypeName,
                                roomType: room.description || '',
                                basePrice: room.price || 0
                              } as Room);
                              setSelectedCheckIn(dailyListSelectedDate);
                              setSelectedCheckOut('');
                              setDateSelectionStep('check-out');
                              setDailyRoomListOpen(false);
                            }}
                          >
                            Giriş Yap
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="daily-room-list-empty">
                  <p>Bu tarihte müsait oda bulunamadı</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiRoomCalendar;
